import type { AppLocale, FunnelCurrency, FunnelRegion, RegionalSettings } from "@/lib/funnel-planner/schema";

export type RegionalPreset = {
  region: FunnelRegion;
  label: string;
  countryCode: string;
  currency: FunnelCurrency;
  taxRatePercent: number;
  shippingCostPerOrder: number;
  defaultMerchantFeePercent: number;
  defaultMerchantFixedFee: number;
};

export const REGIONAL_PRESETS: readonly RegionalPreset[] = [
  { region: "us", label: "United States", countryCode: "US", currency: "USD", taxRatePercent: 0, shippingCostPerOrder: 0, defaultMerchantFeePercent: 2.9, defaultMerchantFixedFee: 0.3 },
  { region: "canada", label: "Canada", countryCode: "CA", currency: "CAD", taxRatePercent: 5, shippingCostPerOrder: 0, defaultMerchantFeePercent: 2.9, defaultMerchantFixedFee: 0.3 },
  { region: "uk", label: "United Kingdom", countryCode: "GB", currency: "GBP", taxRatePercent: 20, shippingCostPerOrder: 0, defaultMerchantFeePercent: 2.9, defaultMerchantFixedFee: 0.2 },
  { region: "eu", label: "European Union", countryCode: "DE", currency: "EUR", taxRatePercent: 19, shippingCostPerOrder: 0, defaultMerchantFeePercent: 2.9, defaultMerchantFixedFee: 0.25 },
  { region: "israel", label: "Israel", countryCode: "IL", currency: "ILS", taxRatePercent: 17, shippingCostPerOrder: 0, defaultMerchantFeePercent: 2.9, defaultMerchantFixedFee: 1 },
  { region: "australia", label: "Australia", countryCode: "AU", currency: "AUD", taxRatePercent: 10, shippingCostPerOrder: 0, defaultMerchantFeePercent: 2.9, defaultMerchantFixedFee: 0.3 },
  { region: "india", label: "India", countryCode: "IN", currency: "INR", taxRatePercent: 18, shippingCostPerOrder: 0, defaultMerchantFeePercent: 2.9, defaultMerchantFixedFee: 3 },
  { region: "brazil", label: "Brazil", countryCode: "BR", currency: "BRL", taxRatePercent: 0, shippingCostPerOrder: 0, defaultMerchantFeePercent: 3.5, defaultMerchantFixedFee: 0.6 },
  { region: "japan", label: "Japan", countryCode: "JP", currency: "JPY", taxRatePercent: 10, shippingCostPerOrder: 0, defaultMerchantFeePercent: 3.2, defaultMerchantFixedFee: 0 },
  { region: "global", label: "Global / Mixed", countryCode: "US", currency: "USD", taxRatePercent: 0, shippingCostPerOrder: 0, defaultMerchantFeePercent: 2.9, defaultMerchantFixedFee: 0.3 },
] as const;

export function getRegionalPreset(region: FunnelRegion | string | undefined): RegionalPreset {
  return REGIONAL_PRESETS.find((preset) => preset.region === region) ?? REGIONAL_PRESETS[0]!;
}

export function createRegionalSettings(region: FunnelRegion = "us"): RegionalSettings {
  const preset = getRegionalPreset(region);
  return {
    region: preset.region,
    countryCode: preset.countryCode,
    currency: preset.currency,
    locale: localeForRegion(preset.region),
    taxRatePercent: preset.taxRatePercent,
    shippingCostPerOrder: preset.shippingCostPerOrder,
    fulfillmentCostPercent: 0,
    defaultMerchantFeePercent: preset.defaultMerchantFeePercent,
    defaultMerchantFixedFee: preset.defaultMerchantFixedFee,
    assumptionsLabel: "benchmark_default",
  };
}

function localeForRegion(region: FunnelRegion): AppLocale {
  switch (region) {
    case "israel":
    case "IL":
      return "he";
    case "india":
    case "IN":
      return "hi";
    case "brazil":
    case "BR":
      return "pt-br";
    case "japan":
    case "JP":
      return "ja";
    case "CN":
      return "zh-cn";
    case "KR":
      return "ko";
    case "MX":
      return "es";
    case "AE":
      return "ar";
    case "EU":
    case "eu":
      return "de";
    default:
      return "en";
  }
}

export function currencyFractionDigits(currency: FunnelCurrency | string): number {
  return currency === "JPY" ? 0 : 0;
}

export function formatRegionalCurrency(
  value: number,
  locale: AppLocale | string,
  settings?: Pick<RegionalSettings, "currency"> | null,
): string {
  const currency = settings?.currency ?? "USD";
  return new Intl.NumberFormat(locale === "pt-br" ? "pt-BR" : locale === "zh-cn" ? "zh-CN" : locale, {
    style: "currency",
    currency,
    maximumFractionDigits: currencyFractionDigits(currency),
  }).format(value);
}
